import React, { useMemo } from 'react';
import classNames from 'classnames';
import { ROUTES } from 'constants/index';
import { useTranslation } from 'services/hooks';
import { buildStructuredList } from 'services/utils';
import { ElementType, formatText } from '..';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import './style.css';

const HtmlElement = ({ data, onRemove, index, insertIndex, editingElementId, handleEditElement, onClickElement }) => {
	const {
		id,
		type,
		content,
		className,
		listTitle,
		nestedIndexes,
		selectedHighlightText,
		linkUrl,
		tableData,
		videoSrc,
		audioSrc,
		imageSrc,
	} = data;

	const TRANSLATION_KEY = 'html_editor';
	const getTranslation = useTranslation();

	const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });

	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
		cursor: 'grab',
	};

	const HtmlElementclassName = classNames('html-element', {
		'selected-insertion': index === insertIndex,
		editing: editingElementId === id,
		video: type === ElementType.VIDEO,
		audio: type === ElementType.AUDIO,
	});

	const handleClick = () => {
		onClickElement && typeof onClickElement === 'function' && onClickElement(index, id);
	};

	const handleEdit = e => {
		e.stopPropagation();
		handleEditElement && typeof handleEditElement === 'function' && handleEditElement(data); // Load element data into the form
	};
	const renderElement = useMemo(() => {
		if ([ElementType.MAIN_TITLE, ElementType.NORMAL_TITLE, ElementType.SUB_TITLE].includes(type)) {
			return React.createElement(type, { className, dangerouslySetInnerHTML: { __html: formatText(content) } });
		}

		switch (type) {
			case ElementType.LINE_BREAK:
				return (
					<>
						<br />
						<br />
					</>
				);

			case ElementType.PARAGRAPH:
				let _formattedText = formatText(content);

				if (selectedHighlightText && linkUrl) {
					const _link = `<a href="${linkUrl}" target="_blank" rel="noopener noreferrer">${selectedHighlightText}</a>`;
					_formattedText = _formattedText?.replace(formatText(selectedHighlightText), _link);
				}

				return <p className={className} dangerouslySetInnerHTML={{ __html: _formattedText }} />;

			case ElementType.ARTICLE:
				return (
					<div className="article">
						{data.title && <h6 className={className} dangerouslySetInnerHTML={{ __html: formatText(data.title) }} />}
						<p dangerouslySetInnerHTML={{ __html: formatText(data.body) }} />
					</div>
				);

			case ElementType.UNORDERED_LIST:
			case ElementType.ORDERED_LIST:
				const ListTag = type;
				const structuredList = buildStructuredList(content, nestedIndexes);
				return (
					<>
						{listTitle && <div className={`list-title ${className}`} dangerouslySetInnerHTML={{ __html: formatText(listTitle) }} />}
						<ListTag>
							{structuredList?.map((item, index) => (
								<li key={index}>
									<span dangerouslySetInnerHTML={{ __html: formatText(item.parent) }}></span>
									{item?.children?.length > 0 && (
										<ListTag>
											{item?.children.map((child, j) => (
												<li key={`${index}-${j}`} dangerouslySetInnerHTML={{ __html: formatText(child) }}></li>
											))}
										</ListTag>
									)}
								</li>
							))}
						</ListTag>
					</>
				);

			case ElementType.MULTIPLE_CHOICE:
				return (
					<div className="multiple-choice">
						<h6 className={className} dangerouslySetInnerHTML={{ __html: formatText(data.questionTitle) }}></h6>
						{data.answers.map((answer, i) => (
							<div key={i} className="multiple-choice-option">
								<input
									type="checkbox"
									id={`choice-${id}-${i}`}
									name={`question-${id}`}
									className={answer.isCorrect ? 'correct-answer' : 'wrong-answer'}
									onChange={e => {
										document.querySelectorAll(`[name="question-${id}"]`).forEach(cb => {
											cb.checked = cb === e.target;
										});
									}}
								/>
								<label htmlFor={`choice-${id}-${i}`} dangerouslySetInnerHTML={{ __html: formatText(answer.text) }}></label>

								{!answer.isCorrect && <p className="hint" dangerouslySetInnerHTML={{ __html: formatText(answer.hint) }}></p>}
							</div>
						))}
					</div>
				);

			case ElementType.TABLE:
				return (
					<table>
						{tableData?.title && <caption className={className}>{tableData?.title}</caption>}
						<thead>
							<tr>
								{tableData?.headers?.map((header, idx) => (
									<th key={idx}>{header}</th>
								))}
							</tr>
						</thead>
						<tbody>
							{Array.from({ length: tableData?.rows[tableData?.headers[0]]?.length }).map((_, rowIdx) => (
								<tr key={rowIdx}>
									{tableData?.headers?.map((header, colIdx) => (
										<td key={colIdx}>{tableData?.rows[header][rowIdx]}</td>
									))}
								</tr>
							))}
						</tbody>
					</table>
				);

			case ElementType.VIDEO:
				const videoFullPath = `${window.location.origin}/app${ROUTES.IFRAME_VIDEO_PLAYER_PAGE}?video_src=${videoSrc}`;
				return <iframe src={videoFullPath} width="90%" height="90%" allowFullScreen />;

			case ElementType.AUDIO:
				const audioFullPath = `${window.location.origin}/app${ROUTES.IFRAME_VIDEO_PLAYER_PAGE}?audio_src=${audioSrc}`;
				return <iframe src={audioFullPath} width="90%" height="90%" />;

			case ElementType.IMAGE:
				return <img src={imageSrc} alt={`image-${id}`} />;

			default:
				return <div className={className} dangerouslySetInnerHTML={{ __html: formatText(content) }}></div>;
		}
	}, [data]);

	const handleRemoveElement = () => {
		onRemove && typeof onRemove === 'function' && onRemove(id);
	};

	return (
		<div className={HtmlElementclassName} onMouseUp={handleClick} ref={setNodeRef} style={style} {...attributes} {...listeners}>
			{renderElement}
			{insertIndex === index && (
				<div className="html-element__btn-container">
					<button className="edit-btn" onMouseUp={handleEdit}>
						{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_element_edit_button`)}
					</button>
					<button className="remove-btn" onMouseUp={handleRemoveElement}>
						X
					</button>
				</div>
			)}
		</div>
	);
};

export default HtmlElement;
